/**
 * Countdown helpers — produce zero-padded "until kickoff" segments and
 * answer the high-level "is this match soon?" question.
 */

import { getLocalDateKey, todayKey } from './comparisons.js';

import { getMatchISODate } from './iso.js';
import { pad } from './_pad.js';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

/**
 * Zero-padded countdown from now to `futureTime`. `days` and `hours` are
 * `null` once they hit zero so leading segments can be hidden; `minutes`
 * and `seconds` are always present. Past targets return all-zero.
 *
 * @param {Date} futureTime
 * @returns {{
 *   days: string | null,
 *   hours: string | null,
 *   minutes: string,
 *   seconds: string,
 * }}
 */
export const getTimeTill = (futureTime) => {
  const total = Math.max(
    0,
    Math.floor((futureTime.getTime() - Date.now()) / 1000)
  );
  const days = Math.floor(total / SECONDS_PER_DAY);
  const hours = Math.floor(total / SECONDS_PER_HOUR) % 24;
  const minutes = Math.floor(total / SECONDS_PER_MINUTE) % 60;
  const seconds = total % 60;

  return {
    days: days > 0 ? pad(days) : null,
    hours: hours > 0 ? pad(hours) : null,
    minutes: pad(minutes),
    seconds: pad(seconds),
  };
};

/**
 * Countdown for an Opta `match`. Reads `match.isoDate` when decorated,
 * falls back to deriving it from `matchInfo`. See {@link getTimeTill}.
 *
 * @param {{ isoDate?: string, matchInfo: { date: string, time: string } }} match
 * @returns {ReturnType<typeof getTimeTill>}
 */
export const getTimeUntilMatch = (match) => {
  const iso = match?.isoDate ?? getMatchISODate(match?.matchInfo);
  return getTimeTill(new Date(iso));
};

/** Threshold below which we collapse a scheduled match to just the time. */
const SOON_HOURS = 12;

/**
 * `true` when a scheduled match is today (local TZ) or kicks off within the
 * next 12 hours — i.e. UIs should show only the kickoff time, no date.
 *
 * @param {{ isoDate?: string, matchInfo?: { date: string, time: string } } | null | undefined} match
 * @returns {boolean}
 */
export const isMatchSoon = (match) => {
  const iso = match?.isoDate ?? getMatchISODate(match?.matchInfo);
  if (!iso) return false;
  if (getLocalDateKey(iso) === todayKey()) return true;
  const { days, hours } = getTimeUntilMatch(match);
  return parseFloat(days ?? '0') === 0 && parseFloat(hours ?? '0') < SOON_HOURS;
};
